import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box, Grid, TextField,
    Button, FormControlLabel, Checkbox, Typography, Stack, Divider, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { API } from '../App';

const RIGHTS = [
    { key: 'read', label: 'Lesen', hint: 'Darf Aufträge und PDFs einsehen' },
    { key: 'write', label: 'Bearbeiten', hint: 'Darf Sicherungseinträge anlegen/kopieren/bearbeiten' },
    { key: 'admin', label: 'Admin', hint: 'Voller Zugriff — Benutzer- und Auftragsverwaltung' }
];

const emptyDraft = { _id: null, name: '', description: '', password: '', rights: [] };

export default function UserModal({ modalRef, onSaved }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('create');
    const [draft, setDraft] = useState(emptyDraft);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (modalRef) modalRef.current = { handleOpen, handleClose };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalRef]);

    const handleOpen = (user) => {
        setError('');
        if (user && user._id) {
            setMode('edit');
            setDraft({
                _id: user._id,
                name: user.name || '',
                description: user.description || '',
                password: '',
                rights: Array.isArray(user.rights) ? [...user.rights] : []
            });
        } else {
            setMode('create');
            setDraft(emptyDraft);
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setError('');
    };

    const toggleRight = (key) => {
        setDraft((d) => {
            const has = d.rights.includes(key);
            return { ...d, rights: has ? d.rights.filter((r) => r !== key) : [...d.rights, key] };
        });
    };

    const handleSubmit = async () => {
        if (!draft.name.trim()) { setError('Name ist erforderlich.'); return; }
        if (mode === 'create' && !draft.password) { setError('Passwort ist erforderlich.'); return; }

        setSaving(true);
        setError('');
        try {
            let res;
            if (mode === 'create') {
                res = await API.createUser({
                    name: draft.name.trim(),
                    description: draft.description,
                    password: draft.password,
                    rights: draft.rights
                });
            } else {
                const payload = {
                    _id: draft._id,
                    name: draft.name.trim(),
                    description: draft.description,
                    rights: draft.rights
                };
                if (draft.password) payload.password = draft.password;
                res = await API.updateUser(payload);
            }
            if (!res?.success) {
                setError(res?.msg || 'Speichern fehlgeschlagen.');
                return;
            }
            if (onSaved) onSaved();
            handleClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
            <DialogTitle sx={{ pr: 6 }}>
                {mode === 'create' ? 'Neuen Benutzer anlegen' : `Benutzer bearbeiten: ${draft.name}`}
                <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField
                        label="Benutzername"
                        required
                        fullWidth
                        size="medium"
                        autoFocus={mode === 'create'}
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    />
                    <TextField
                        label="Anzeigename"
                        fullWidth
                        size="medium"
                        value={draft.description}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                        helperText="Vollständiger Name für Anzeige in Aufträgen."
                    />
                    {mode === 'create' && (
                        <TextField
                            label="Passwort"
                            type="password"
                            required
                            fullWidth
                            size="medium"
                            value={draft.password}
                            onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                            autoComplete="new-password"
                        />
                    )}
                    <Divider />
                    <Typography variant="subtitle2" color="text.secondary">Berechtigungen</Typography>
                    <Grid container spacing={0.5}>
                        {RIGHTS.map((r) => (
                            <Grid size={12} key={r.key}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={draft.rights.includes(r.key)}
                                            onChange={() => toggleRight(r.key)}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography component="span" sx={{ fontWeight: 600 }}>{r.label}</Typography>
                                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                {r.hint}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                    {error && <Typography color="error" variant="body2">{error}</Typography>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} disabled={saving}>Abbrechen</Button>
                <Button onClick={handleSubmit} variant="contained" startIcon={<SaveIcon />} disabled={saving}>
                    {saving ? 'Speichert…' : 'Speichern'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
